// frontend/src/features/ups/UpsRequestsDriverList.js
// Phase 8.7D: UPS "Requests" tab
//
// Purpose:
// - Show assignment requests created by agencies (status = 'requested').
// - UPS can approve them -> status transitions requested -> confirmed.
// - Once approved, they disappear from Requests and appear in Reserved.
//
// Data source:
// - Uses the same UPS site assignments read model as Reserved/Active.
// - Just passes statuses: ['requested'].
//
// Phase 8.7D cleanup:
// - Do NOT pass agencyId/search into loadSiteAssignments payload (thunk reads from Redux).
// - Keep effect deps on shared filter fields so refetch occurs when filters change.

// Phase 09 note:
// - This tab is intentionally NOT time-bucket-specific.
// - Requests is an approval queue, not a time-sliced operational lane.
// - Primary truth here is assignment_status = 'requested'.
// - Therefore we pass an explicit broad query:
//     statuses: ['requested']
//     timeBucket: undefined
// - This makes the intent clear during Phase 09 cleanup:
//   time filtering is intentionally not applied here yet.
// - If later needed, date/time filtering can be added as a refinement
//   without changing the core doctrine of this page.

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Eye } from 'lucide-react'; // ✅ Added

import { loadSiteAssignments, makeSelectAssignmentsForTab } from '../assignments/assignmentsSlice';
import { selectSelectedSiteId } from '../sites/sitesSlice';
import { selectFilters, bumpStatsRefreshKey } from '../ui/filtersSlice';
import { upsertDriver } from '../drivers/driverSlice'; // ✅ Added

import { approveAssignment, cancelAssignment } from '../../services/assignmentService';
import { formatOwnerLabel } from './utils/formatOwnerLabel';
import DriverDetailsModal from './modals/DriverDetailsModal'; // ✅ Added

const REQUEST_ASSIGNMENT_QUERY = {
  statuses: ['requested'],
  timeBucket: undefined,
};

export default function UpsRequestsDriverList() {
  const dispatch = useDispatch();

  const siteId = useSelector(selectSelectedSiteId);
  const filters = useSelector(selectFilters);

  const selectRequested = useMemo(() => makeSelectAssignmentsForTab('requested'), []);
  const requested = useSelector(selectRequested);

  const [acting, setActing] = useState(() => new Set());

  // ✅ 1. Details Modal State
  const [showDetails, setShowDetails] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);

  useEffect(() => {
    if (!siteId) return;
    dispatch(loadSiteAssignments({ siteId, ...REQUEST_ASSIGNMENT_QUERY }));
  }, [dispatch, siteId, filters?.agencyId, filters?.q, filters?.source]);

  const rows = useMemo(() => {
    return (requested || []).filter((r) => !r?.is_banned);
  }, [requested]);

  // ✅ 2. Modal Helpers
  const openDetails = (row) => {
    // Sync to drivers slice so the modal can read "live" data (photos, etc)
    dispatch(upsertDriver(row));
    setDetailsRow(row);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setDetailsRow(null);
  };

  async function refresh() {
    if (!siteId) return;
    dispatch(loadSiteAssignments({ siteId, ...REQUEST_ASSIGNMENT_QUERY }));
    dispatch(bumpStatsRefreshKey());
  }

  const onApprove = async (assignmentId) => {
    if (!siteId || acting.has(assignmentId)) return;
    setActing((prev) => new Set(prev).add(assignmentId));
    try {
      await approveAssignment(siteId, assignmentId);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.error || 'Approve failed');
      await refresh();
    } finally {
      setActing((prev) => {
        const next = new Set(prev);
        next.delete(assignmentId);
        return next;
      });
    }
  };

  const onReject = async (assignmentId) => {
    if (!siteId || acting.has(assignmentId)) return;
    setActing((prev) => new Set(prev).add(assignmentId));
    try {
      await cancelAssignment(siteId, assignmentId);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.error || 'Reject failed');
      await refresh();
    } finally {
      setActing((prev) => {
        const next = new Set(prev);
        next.delete(assignmentId);
        return next;
      });
    }
  };

  return (
    <div className="my-6">
      <div className="mb-4">
        <h3 className="text-2xl font-bold text-gray-800">Pending Requests</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
          <thead className="bg-gray-50">
            <tr>
              {['Name', 'Vehicle Reg.', 'Owner', 'Submitted', 'Actions'].map((col) => (
                <th key={col} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {rows.length === 0 ? (
              <tr>
                <td className="px-6 py-6 text-sm text-gray-500" colSpan={5}>No pending requests.</td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.assignment_id || r.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{r?.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{r?.vehicle_registration_number}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{formatOwnerLabel(r)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {r?.created_at ? new Date(r.created_at).toLocaleString() : '-'}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-3">
                      {/* ✅ 3. Eye Button Added */}
                      <button
                        onClick={() => openDetails(r)}
                        className="p-1 rounded hover:bg-gray-100 text-blue-600"
                        title="View details"
                      >
                        <Eye size={18} />
                      </button>

                      <button
                        onClick={() => onApprove(Number(r.assignment_id || r.id))}
                        disabled={acting.has(Number(r.assignment_id || r.id))}
                        className={`px-3 py-1 rounded bg-green-600 text-white text-xs hover:bg-green-700 ${
                          acting.has(Number(r.assignment_id || r.id)) ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                      >
                        Approve
                      </button>

                      <button
                        onClick={() => onReject(Number(r.assignment_id || r.id))}
                        disabled={acting.has(Number(r.assignment_id || r.id))}
                        className={`px-3 py-1 rounded bg-gray-200 text-gray-900 text-xs hover:bg-gray-300 ${
                          acting.has(Number(r.assignment_id || r.id)) ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ✅ 4. Details Modal Rendered */}
      {showDetails && detailsRow && (
        <DriverDetailsModal
          driver={detailsRow}
          onClose={closeDetails}
        />
      )}
    </div>
  );
}